import { useI18n } from "@/i18n/I18nProvider";

/**
 * EsploratoreBadge — turista rewards section.
 * Introduces the Explorer badge (tiers) + partner perks redeemable with points.
 * Fills the P0 gap: "cosa ci fai con i punti + Badge Esploratore".
 */
function TierLadder({ t, tiers }) {
    return (
        <div
            className="itly-tier-grid"
            data-testid="esploratore-tiers"
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 18,
                marginBottom: 44,
            }}
        >
            {tiers.map((tier) => (
                <div
                    key={tier.key}
                    data-testid={`tier-${tier.key}`}
                    style={{
                        background: "rgba(255, 255, 255, 0.04)",
                        border: "1px solid rgba(232, 113, 76, 0.22)",
                        borderRadius: 18,
                        padding: "26px 24px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 16,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 999,
                            background: tier.tint,
                            color: "var(--itly-fg-strong)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "Fraunces, serif",
                            fontSize: 22,
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {tier.glyph}
                    </span>
                    <div>
                        <div
                            style={{
                                fontFamily: "Fraunces, serif",
                                fontSize: 22,
                                color: "#F5EEDF",
                                marginBottom: 4,
                            }}
                        >
                            {t(`esploratore.${tier.key}.name`)}
                        </div>
                        <div
                            style={{
                                fontFamily: "Inter, sans-serif",
                                fontSize: 13.5,
                                color: "rgba(237, 231, 220, 0.7)",
                                lineHeight: 1.5,
                            }}
                        >
                            {t(`esploratore.${tier.key}.desc`)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PerkList({ t, perks }) {
    return (
        <div
            style={{
                background: "rgba(255, 255, 255, 0.045)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 20,
                padding: "28px 30px",
            }}
        >
            <div
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "var(--itly-orange-500)",
                    fontWeight: 600,
                    marginBottom: 18,
                }}
            >
                {t("esploratore.perkTitle")}
            </div>
            <div
                className="itly-perk-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 14,
                }}
            >
                {perks.map((p) => (
                    <div
                        key={p}
                        data-testid={`perk-${p}`}
                        style={{
                            background: "rgba(232, 113, 76, 0.08)",
                            borderRadius: 12,
                            padding: "16px 18px",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            color: "#F5EEDF",
                            lineHeight: 1.35,
                            borderLeft: "3px solid var(--itly-orange-500)",
                        }}
                    >
                        {t(`esploratore.${p}`)}
                    </div>
                ))}
            </div>
            <div
                style={{
                    marginTop: 22,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12.5,
                    color: "rgba(237, 231, 220, 0.55)",
                    fontStyle: "italic",
                }}
            >
                {t("esploratore.footnote")}
            </div>
        </div>
    );
}

export default function EsploratoreBadge() {
    const { t } = useI18n();
    const tiers = [
        { key: "tier1", glyph: "◐", tint: "#F6D8CB" },
        { key: "tier2", glyph: "◑", tint: "#EDB59F" },
        { key: "tier3", glyph: "●", tint: "#E8714C" },
    ];
    const perks = ["perk1", "perk2", "perk3", "perk4"];

    return (
        <section
            id="esploratore"
            data-testid="esploratore"
            data-reveal="up"
            style={{
                background: "var(--itly-teal-900)",
                color: "#EDE7DC",
                padding: "96px 32px",
            }}
        >
            <div style={{ maxWidth: 1120, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <div
                        className="itly-badge"
                        style={{
                            background: "rgba(232, 113, 76, 0.14)",
                            color: "#F4B99E",
                            borderColor: "rgba(232, 113, 76, 0.32)",
                        }}
                    >
                        {t("esploratore.badge")}
                    </div>
                    <h2
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: "clamp(32px, 4vw, 52px)",
                            lineHeight: 1.05,
                            marginTop: 20,
                            marginBottom: 18,
                            color: "#F5EEDF",
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {t("esploratore.title_a")}
                        <br />
                        <em
                            style={{
                                color: "var(--itly-orange-500)",
                                fontStyle: "italic",
                            }}
                        >
                            {t("esploratore.title_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            maxWidth: 720,
                            margin: "0 auto",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: 1.65,
                            color: "rgba(237, 231, 220, 0.78)",
                        }}
                    >
                        {t("esploratore.sub")}
                    </p>
                </div>

                {/* Tier ladder */}
                <TierLadder t={t} tiers={tiers} />

                {/* Perks */}
                <PerkList t={t} perks={perks} />
            </div>
        </section>
    );
}
